<template>
  <div v-if="hasAnyFeatures">
    <v-expansion-panels variant="accordion">
      <v-expansion-panel
        v-for="(category, key) in features"
        :key="key"
        v-show="category.items.length > 0"
      >
        <v-expansion-panel-title>
          <div class="d-flex align-center">
            <v-icon :icon="category.icon" size="small" class="mr-2" />
            {{ category.title }}
            <v-chip size="x-small" class="ml-2">{{
              category.items.length
            }}</v-chip>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-list density="compact">
            <v-list-item v-for="(item, index) in category.items" :key="index">
              <template v-slot:prepend>
                <v-icon size="x-small" color="success">mdi-check</v-icon>
              </template>
              <v-list-item-title>{{ item }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
  <v-alert v-else type="info" variant="tonal">
    <v-alert-title>Standard Equipment</v-alert-title>
    <div class="mt-2">
      <p class="mb-2">
        This trim includes comprehensive standard equipment. Key features
        include:
      </p>
      <v-list density="compact">
        <v-list-item v-for="(feature, index) in generalFeatures" :key="index">
          <template v-slot:prepend>
            <v-icon size="x-small" color="success">mdi-check-circle</v-icon>
          </template>
          <v-list-item-title>{{ feature }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </div>
  </v-alert>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { TrimData } from '@/types'

interface FeatureCategory {
  title: string
  icon: string
  items: string[]
}

const props = defineProps<{
  trim: TrimData | null
}>()

const getAllFeatures = (): Record<string, FeatureCategory> => {
  const allFeatures: Record<string, FeatureCategory> = {
    safety: { title: 'Safety', icon: 'mdi-shield-check', items: [] },
    comfort: {
      title: 'Comfort & Convenience',
      icon: 'mdi-seat-recline-extra',
      items: [],
    },
    interior: { title: 'Interior', icon: 'mdi-car-seat', items: [] },
    exterior: { title: 'Exterior', icon: 'mdi-car', items: [] },
    technology: { title: 'Technology', icon: 'mdi-chip', items: [] },
    entertainment: { title: 'Entertainment', icon: 'mdi-music', items: [] },
    optionPackage: {
      title: 'Option Package',
      icon: 'mdi-package-variant',
      items: [],
    },
  }

  if (props.trim?.standardEquipment) {
    const stdEquip = props.trim.standardEquipment

    if (stdEquip.SAFETY?.items) allFeatures.safety.items = stdEquip.SAFETY.items
    if (stdEquip.COMFORT?.items)
      allFeatures.comfort.items = stdEquip.COMFORT.items
    if (stdEquip.INTERIOR?.items)
      allFeatures.interior.items = stdEquip.INTERIOR.items
    if (stdEquip.EXTERIOR?.items)
      allFeatures.exterior.items = stdEquip.EXTERIOR.items
    if (stdEquip.TECHNOLOGY?.items)
      allFeatures.technology.items = stdEquip.TECHNOLOGY.items
    if (stdEquip.ENTERTAINMENT?.items)
      allFeatures.entertainment.items = stdEquip.ENTERTAINMENT.items

    if (stdEquip['Option Package']?.items) {
      allFeatures.optionPackage.items = stdEquip['Option Package'].items
    }

    Object.keys(stdEquip).forEach(key => {
      if (stdEquip[key]?.items && Array.isArray(stdEquip[key].items)) {
        stdEquip[key].items.forEach((item: string) => {
          const itemLower = item.toLowerCase()

          if (
            itemLower.includes('airbag') ||
            itemLower.includes('brake') ||
            itemLower.includes('safety') ||
            itemLower.includes('collision') ||
            itemLower.includes('alert') ||
            itemLower.includes('assist') ||
            itemLower.includes('control') ||
            itemLower.includes('camera')
          ) {
            allFeatures.safety.items.push(item)
          } else if (
            itemLower.includes('seat') ||
            itemLower.includes('climate') ||
            itemLower.includes('heated') ||
            itemLower.includes('cooled') ||
            itemLower.includes('steering') ||
            itemLower.includes('keyless') ||
            itemLower.includes('remote')
          ) {
            allFeatures.comfort.items.push(item)
          } else if (
            itemLower.includes('display') ||
            itemLower.includes('screen') ||
            itemLower.includes('carplay') ||
            itemLower.includes('android auto') ||
            itemLower.includes('bluetooth') ||
            itemLower.includes('usb') ||
            itemLower.includes('navigation') ||
            itemLower.includes('sync')
          ) {
            allFeatures.technology.items.push(item)
          } else if (
            itemLower.includes('audio') ||
            itemLower.includes('speaker') ||
            itemLower.includes('sound') ||
            itemLower.includes('radio')
          ) {
            allFeatures.entertainment.items.push(item)
          } else if (
            itemLower.includes('wheel') ||
            itemLower.includes('tire') ||
            itemLower.includes('mirror') ||
            itemLower.includes('light') ||
            itemLower.includes('paint') ||
            itemLower.includes('bumper')
          ) {
            allFeatures.exterior.items.push(item)
          } else if (
            itemLower.includes('leather') ||
            itemLower.includes('fabric') ||
            itemLower.includes('trim') ||
            itemLower.includes('console')
          ) {
            allFeatures.interior.items.push(item)
          } else if (!allFeatures.optionPackage.items.includes(item)) {
            allFeatures.optionPackage.items.push(item)
          }
        })
      }
    })
  }

  Object.keys(allFeatures).forEach(key => {
    allFeatures[key].items = [...new Set(allFeatures[key].items)]
  })

  return allFeatures
}

const features = computed(() => getAllFeatures())

const hasAnyFeatures = computed(() => {
  return Object.values(features.value).some(
    (cat: FeatureCategory) => cat.items.length > 0,
  )
})

const generalFeatures = computed(() => {
  const features = []

  if (props.trim?.technicalSpecification) {
    const specs = props.trim.technicalSpecification

    if (specs.Engine?.items?.[0]) {
      features.push(`Engine: ${specs.Engine.items[0]}`)
    }
    if (specs.Transmission?.items?.length > 0) {
      specs.Transmission.items
        .slice(0, 2)
        .forEach((item: string) => features.push(item))
    }
    if (specs.Mileage?.items?.length > 0) {
      specs.Mileage.items
        .slice(0, 2)
        .forEach((item: string) => features.push(item))
    }
    if (specs['Interior Dimensions']?.items?.length > 0) {
      specs['Interior Dimensions'].items
        .slice(0, 3)
        .forEach((item: string) => features.push(item))
    }
  }

  if (features.length === 0) {
    features.push(
      'Anti-lock Braking System (ABS)',
      'Electronic Stability Control',
      'Traction Control',
      'Front and Rear Airbags',
      'Air Conditioning',
      'Power Windows and Locks',
      'AM/FM Stereo System',
      'Rearview Camera',
    )
  }

  return features.slice(0, 12)
})
</script>
